<template>
  <Certifications
    label
    :category="0"
    :elementId="elementId"
    :propCertifications="certificationIds"
    useIdOnly
    :propFetchCertifications="false"
    :rules="rules"
    :hint="hint"
    @certifications="$emit('certifications', $event)">
    <template #selection="{ item, parent, selected }">
      <v-chip
        v-if="!requiresBasis(item)"
        :input-value="selected"
        close
        @click:close="parent.selectItem(item)">
        {{ item.name }}
      </v-chip>
      <v-menu v-else offset-y max-height="320" :close-on-content-click="true">
        <template #activator="{ on }">
          <v-chip
            v-on="on"
            :input-value="selected"
            :color="selectionFor(item) ? undefined : 'warning'"
            :outlined="!selectionFor(item)"
            :data-testid="`tract-certification-basis-${item.certificationId}`"
            close
            @click.stop
            @click:close="parent.selectItem(item)">
            <v-icon v-if="!selectionFor(item)" small left>mdi-alert</v-icon>
            {{ chipText(item) }}
          </v-chip>
        </template>
        <v-list dense>
          <v-progress-linear v-if="loading" indeterminate/>
          <v-list-item v-else-if="basisItemsFor(item).length === 0" disabled>
            <v-list-item-content>
              <v-list-item-title class="text-wrap">{{ $t('noEligibleBasisAccountCertifications') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <template v-for="basisItem in basisItemsFor(item)">
            <v-subheader v-if="basisItem.header" :key="basisItem.header">{{ basisItem.header }}</v-subheader>
            <v-list-item
              v-else
              :key="basisItem.value"
              :disabled="basisItem.disabled"
              :input-value="selectionKey(item.certificationId) === basisItem.value"
              @click="basisChosen(item.certificationId, basisItem.value)">
              <v-list-item-content>
                <v-list-item-title>{{ basisItem.text }}</v-list-item-title>
                <v-list-item-subtitle>{{ basisItem.subtitle }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <Icon
                  class="basis-status-icon"
                  :icon="basisItem.statusIcon.icon"
                  :iconColor="basisItem.statusIcon.color"
                  :tooltipText="basisItem.statusIcon.tooltipText"
                  :propagateClick="!basisItem.disabled"
                  :small="false"
                  margin=""/>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </v-menu>
    </template>
  </Certifications>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { accountCertificationLabel } from '@/utils/AccountCertifications.js'
import { CERTIFICATION_EXPIRY_WARNING_DAYS } from '@/utils/constants.js'
import { CertificationStatus } from '@/utils/Enumerations.js'

export default {
  name: 'TractCertificationBases',

  components: {
    Certifications: () => import('@/components/settings-components/certification/Certifications.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    certificationIds: {
      type: Array,
      default: () => []
    },
    accountIds: {
      type: Array,
      default: () => []
    },
    propAssignments: {
      type: Array,
      default: () => []
    },
    elementId: {
      type: String,
      default: undefined
    },
    rules: {
      type: Array,
      default: () => []
    },
    hint: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    loading: false,
    accountCertificationsByAccount: {},
    selections: {}
  }),

  computed: {
    ...mapGetters('account', ['allAccounts']),

    uniqueAccountIds () {
      return [...new Set(this.accountIds.filter(id => id != null))]
    },

    assignments () {
      return this.certificationIds.map(certificationId => ({
        certificationId,
        basisAccountCertificationId: this.selections[certificationId] ?? null
      }))
    }
  },

  watch: {
    uniqueAccountIds: {
      handler () {
        this.loadAccountCertifications()
      },
      immediate: true
    },

    certificationIds () {
      this.pruneSelections()
    },

    propAssignments: {
      handler (val) {
        if (JSON.stringify(val ?? []) === JSON.stringify(this.assignments)) return
        this.applyAssignments(val)
      },
      immediate: true
    },

    assignments (val) {
      this.$emit('assignments-changed', val)
    }
  },

  methods: {
    ...mapActions('certification', ['fetchAccountCertificationsByAccountId']),

    requiresBasis (certification) {
      return (certification.basisCertificationIds?.length ?? 0) > 0
    },

    applyAssignments (assignments) {
      const selections = {}
      for (const a of assignments ?? []) {
        if (a.basisAccountCertificationId != null) {
          selections[a.certificationId] = a.basisAccountCertificationId
        }
      }
      this.selections = selections
    },

    async loadAccountCertifications () {
      const missing = this.uniqueAccountIds.filter(id => this.accountCertificationsByAccount[id] === undefined)
      if (missing.length > 0) {
        this.loading = true
        try {
          const results = await Promise.all(missing.map(id => this.fetchAccountCertificationsByAccountId(id)))
          const loaded = { ...this.accountCertificationsByAccount }
          missing.forEach((id, i) => { loaded[id] = results[i] ?? [] })
          this.accountCertificationsByAccount = loaded
        } finally {
          this.loading = false
        }
      }
      this.pruneSelections()
    },

    // a basis that has not loaded yet is kept until the load settles, after which an unresolvable one belongs to an account no longer on the tract
    pruneSelections () {
      const pruned = {}
      for (const [certificationId, accountCertificationId] of Object.entries(this.selections)) {
        const basis = this.accountCertification(accountCertificationId)
        const onTract = basis ? this.uniqueAccountIds.includes(basis.accountId) : this.loading
        if (this.certificationIds.includes(Number(certificationId)) && onTract) {
          pruned[certificationId] = accountCertificationId
        }
      }
      if (Object.keys(pruned).length !== Object.keys(this.selections).length) {
        this.selections = pruned
      }
    },

    accountName (accountId) {
      return this.allAccounts.find(a => a.accountId === accountId)?.name ?? this.$t('account')
    },

    selectionFor (certification) {
      return this.selections[certification.certificationId]
    },

    chipText (certification) {
      const accountCertificationId = this.selectionFor(certification)
      if (!accountCertificationId) return `${certification.name} · ${this.$t('selectBasis')}`
      const basis = this.accountCertification(accountCertificationId)
      if (!basis) return certification.name
      return `${certification.name} · ${this.accountName(basis.accountId)} · ${accountCertificationLabel(basis)}`
    },

    accountCertification (accountCertificationId) {
      return Object.values(this.accountCertificationsByAccount).flat().find(ac => ac.accountCertificationId === accountCertificationId)
    },

    basisItemsFor (certification) {
      const items = []
      for (const accountId of this.uniqueAccountIds) {
        const eligible = (this.accountCertificationsByAccount[accountId] ?? [])
          .filter(ac => certification.basisCertificationIds.includes(ac.certificationId))
          .sort((a, b) => a.name.localeCompare(b.name))
        if (eligible.length === 0) continue

        items.push({ header: this.accountName(accountId) })
        for (const ac of eligible) {
          const status = this.basisStatus(ac)
          items.push({
            value: ac.accountCertificationId,
            text: ac.name,
            subtitle: this.subtitleFor(ac, status),
            disabled: status === CertificationStatus.Inactive.value,
            statusIcon: this.statusIcon(status)
          })
        }
      }
      return items
    },

    basisStatus (accountCertification) {
      const now = moment.utc()
      const criticalDate = moment.utc().add(CERTIFICATION_EXPIRY_WARNING_DAYS, 'd')
      const effectiveDate = accountCertification.effectiveDate ? moment.utc(accountCertification.effectiveDate) : undefined
      const expirationDate = accountCertification.expirationDate ? moment.utc(accountCertification.expirationDate) : undefined
      if (effectiveDate?.isAfter(now)) return CertificationStatus.Inactive.value
      if (expirationDate?.isSameOrBefore(now)) return CertificationStatus.Expired.value
      if (expirationDate?.isBefore(criticalDate)) return CertificationStatus.Expiring.value
      return CertificationStatus.Active.value
    },

    statusIcon (status) {
      switch (status) {
        case CertificationStatus.Inactive.value:
          return { icon: 'mdi-clock-outline', color: 'grey', tooltipText: this.$t('notYetEffective') }
        case CertificationStatus.Expiring.value:
          return { icon: 'mdi-alert', color: 'warning', tooltipText: this.$t('expiringSoon') }
        case CertificationStatus.Expired.value:
          return { icon: 'mdi-close-circle', color: 'error', tooltipText: this.$t('expired') }
        default:
          return { icon: 'mdi-checkbox-marked-circle', color: 'success', tooltipText: this.$t('active') }
      }
    },

    subtitleFor (accountCertification, status) {
      const parts = []
      if (accountCertification.certificateNumber) parts.push(`#${accountCertification.certificateNumber}`)
      if (status === CertificationStatus.Inactive.value) {
        parts.push(`${this.$t('effective')} ${utcToLocalDate(accountCertification.effectiveDate)}`)
      } else if (accountCertification.expirationDate) {
        const expired = status === CertificationStatus.Expired.value
        parts.push(`${expired ? this.$t('expiredOn') : this.$t('expiresOnVerbatim')} ${utcToLocalDate(accountCertification.expirationDate)}`)
      }
      return parts.join(' · ')
    },

    selectionKey (certificationId) {
      return this.selections[certificationId] ?? null
    },

    basisChosen (certificationId, accountCertificationId) {
      this.selections = { ...this.selections, [certificationId]: accountCertificationId }
    }
  }
}
</script>

<style scoped>
.v-list-item--disabled .basis-status-icon {
  pointer-events: auto;
}
</style>
